import React, { useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Vehicle, VehiclePhoto } from '../../types/vehicle';
import { doc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface VehiclePhotoUploaderProps {
  vehicle: Vehicle;
  onUploaded?: () => void;
}

const VehiclePhotoUploader: React.FC<VehiclePhotoUploaderProps> = ({ vehicle, onUploaded }) => {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A fájl mérete maximum 5 MB lehet');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Csak kép fájlokat lehet feltölteni');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      // Fájl feltöltése Storage-ba
      const fileName = `vehicles/${vehicle.id}/${Date.now()}_${selectedFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, selectedFile);
      const downloadUrl = await getDownloadURL(storageRef);

      // Fotó hozzáadása a járműhöz
      const photo: Omit<VehiclePhoto, 'id'> = {
        url: downloadUrl,
        uploadedAt: new Date(),
        description: description || undefined,
        size: selectedFile.size
      };

      await updateDoc(doc(db, 'vehicles', vehicle.id), {
        photos: arrayUnion({
          ...photo,
          id: Date.now().toString()
        }),
        updatedAt: serverTimestamp()
      });

      toast.success('Fotó feltöltve');
      setSelectedFile(null);
      setDescription('');
      setPreviewUrl(null);
      if (onUploaded) onUploaded();
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
        Fotó feltöltése
      </h3>

      {!selectedFile ? (
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <label htmlFor="photo-upload" className="cursor-pointer">
              <span className="mt-2 block text-sm font-medium text-primary-600 hover:text-primary-500">
                Válassz egy fájlt
              </span>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="sr-only"
              />
            </label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              PNG, JPG, GIF max. 5MB
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative">
            <img
              src={previewUrl || ''}
              alt="Preview"
              className="w-full h-48 object-cover rounded-lg"
            />
            <button
              onClick={() => {
                setSelectedFile(null);
                setPreviewUrl(null);
                setDescription('');
              }}
              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Leírás (opcionális)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Pl. Bal oldal, kár, stb..."
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
            />
          </div>

          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {uploading ? 'Feltöltés...' : 'Feltöltés'}
          </button>
        </div>
      )}

      {/* Meglévő fotók */}
      {vehicle.photos && vehicle.photos.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Meglévő fotók ({vehicle.photos.length})
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {vehicle.photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.url}
                  alt={photo.description || 'Vehicle photo'}
                  className="w-full h-32 object-cover rounded-lg"
                />
                {photo.description && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                    {photo.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclePhotoUploader;
